export const CHAT_PROFANITY_ERROR = 'Messages containing inappropriate language cannot be sent.';

const NORMALIZATION_CHAR_MAP: Record<string, string> = {
  '0': 'o',
  '1': 'i',
  '3': 'e',
  '4': 'a',
  '5': 's',
  '7': 't',
  '@': 'a',
  $: 's',
};

const ALLOWLIST_WORDS = new Set([
  'assistant',
  'assistants',
  'assignment',
  'assignments',
  'class',
  'classes',
  'classic',
  'classical',
  'curve',
  'curved',
  'curves',
  'curvature',
  'future',
  'futures',
  'passion',
  'passionate',
  'pulover',
  'pulovere',
  'scunthorpe',
]);

const CHAT_PROFANITY_TOKEN_PATTERNS = [
  /^f+u+c+k+$/,
  /^f+u+c+k+e+r+s?$/,
  /^f+u+c+k+e+d$/,
  /^f+u+c+k+i+n+g$/,
  /^m+o+t+h+e+r+f+u+c+k+e+r+s?$/,
  /^s+h+i+t+$/,
  /^s+h+i+t+t*y$/,
  /^b+i+t+c+h+(e+s)?$/,
  /^b+a+s+t+a+r+d+s?$/,
  /^a+s+s+h+o+l+e+s?$/,
  /^d+i+c+k+(h+e+a+d+s?)?$/,
  /^c+u+n+t+s?$/,
  /^w+h+o+r+e+s?$/,
  /^s+l+u+t+s?$/,
  /^p+u+s+s+y$/,
  /^w+t+f+$/,
  /^n+i+g+g+(a|a+s|e+r|e+r+s)$/,
  /^m+u+i+e+$/,
  /^m+u+i+s+t+[a-z]*$/,
  /^p+i+z+d+[a-z]*$/,
  /^c+u+r+v+[a-z]*$/,
  /^c+o+a+i+e+([a-z]*)$/,
  /^p+u+l+(a|e|i+i|i+|ic+a?)$/,
  /^f+u+t+$/,
  /^f+u+t+u+t+[a-z]*$/,
  /^f+u+t+e(m|t+i|t+i+i)?$/,
  /^f+u+t+i$/,
  /^d+r+a+c+u(l+u+i)?$/,
] as const;

const CHAT_PROFANITY_EMBEDDED_PATTERNS = [
  /f+u+c+k+/,
  /m+o+t+h+e+r+f+u+c+k+e+r+/,
  /s+h+i+t+/,
  /b+i+t+c+h+/,
  /b+a+s+t+a+r+d+/,
  /a+s+s+h+o+l+e+/,
  /d+i+c+k+h+e+a+d+/,
  /c+u+n+t+/,
  /w+h+o+r+e+/,
  /s+l+u+t+/,
  /p+u+s+s+y+/,
  /w+t+f+/,
  /n+i+g+g+(a|e+r)/,
  /m+u+i+e+/,
  /m+u+i+s+t+/,
  /p+i+z+d+/,
  /c+o+a+i+e+/,
  /p+u+l+(a|i+i|i+|a+m+e+a)/,
  /d+r+a+c+u(l+u+i)?/,
] as const;

function normalizeChar(char: string): string {
  return NORMALIZATION_CHAR_MAP[char] ?? char;
}

export function normalizeChatModerationText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[013457@$]/g, (char) => normalizeChar(char))
    .replace(/[ăâ]/g, 'a')
    .replace(/î/g, 'i')
    .replace(/[șş]/g, 's')
    .replace(/[țţ]/g, 't')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenizeChatModerationText(text: string): string[] {
  const normalized = normalizeChatModerationText(text);
  return normalized ? normalized.split(' ') : [];
}

function buildJoinedSingleCharacterCandidates(tokens: string[]): string[] {
  const joined: string[] = [];
  let current = '';

  for (const token of tokens) {
    if (/^[a-z0-9]$/.test(token)) {
      current += token;
      continue;
    }

    if (current.length >= 3) {
      joined.push(current);
    }
    current = '';
  }

  if (current.length >= 3) {
    joined.push(current);
  }

  return joined;
}

export function getChatModerationCandidates(text: string): string[] {
  const tokens = tokenizeChatModerationText(text);
  const compacted = tokens.join('');
  const candidates = new Set<string>([
    ...tokens,
    ...buildJoinedSingleCharacterCandidates(tokens),
    compacted,
  ]);
  return Array.from(candidates).filter(Boolean);
}

function matchesProfanityPattern(candidate: string): boolean {
  if (ALLOWLIST_WORDS.has(candidate)) return false;
  return (
    CHAT_PROFANITY_TOKEN_PATTERNS.some((pattern) => pattern.test(candidate)) ||
    CHAT_PROFANITY_EMBEDDED_PATTERNS.some((pattern) => pattern.test(candidate))
  );
}

export function containsProfanity(text: string): boolean {
  return getChatModerationCandidates(text).some((candidate) =>
    matchesProfanityPattern(candidate)
  );
}

export function validateChatMessage(text: string): string | null {
  if (!text.trim()) return null;
  return containsProfanity(text) ? CHAT_PROFANITY_ERROR : null;
}
